import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, Wand2, Info,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const GREEN = '#0F6E56';

const KIND_META = {
  inactive_with_activity: {
    label: 'Status atrasado — tem atividade',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: AlertTriangle,
    description: 'Projeto marcado como "Não Iniciado" mas já tem tarefas em andamento ou concluídas.',
    canAutoFix: true,
  },
  active_without_activity: {
    label: 'Status adiantado — sem atividade',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: Info,
    description: 'Projeto marcado como "Em Andamento" mas todas as tarefas ainda estão "não iniciadas".',
    canAutoFix: true,
  },
  done_with_open_tasks: {
    label: 'Concluído com tarefas abertas',
    color: 'bg-red-50 text-red-700 border-red-200',
    icon: AlertTriangle,
    description: 'Projeto marcado como "Concluído" mas ainda há tarefas abertas.',
    canAutoFix: true,
  },
  done_suggested: {
    label: 'Sugestão: pronto para concluir',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
    description: 'Todas as tarefas estão concluídas. Sugestão (nunca aplicada automaticamente).',
    canAutoFix: false,
  },
};

function KindGroup({ kind, items, selectedIds, onToggle, onToggleAll, expanded, onExpand }) {
  const meta = KIND_META[kind] || {
    label: kind, color: 'bg-gray-50 text-gray-700 border-gray-200',
    icon: Info, description: '', canAutoFix: false,
  };
  const Icon = meta.icon;
  const allSelected = items.every((it) => selectedIds.has(it.project_id));
  const someSelected = items.some((it) => selectedIds.has(it.project_id));

  return (
    <Card className={`border ${meta.color.split(' ').slice(-1)[0]} overflow-hidden`} data-testid={`diag-group-${kind}`}>
      <button
        type="button"
        className="w-full px-5 py-4 flex items-center gap-3 hover:bg-gray-50/60 transition-colors text-left"
        onClick={() => onExpand(kind)}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${meta.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">{meta.label}</div>
          <div className="text-xs text-gray-500">{meta.description}</div>
        </div>
        <Badge className={`${meta.color} border text-[11px] shrink-0`}>{items.length}</Badge>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {meta.canAutoFix && (
            <div className="px-5 py-2 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => onToggleAll(kind, !allSelected)}
                data-testid={`diag-toggle-all-${kind}`}
              />
              <span className="text-xs text-gray-600">
                {allSelected ? 'Desmarcar todos' : someSelected ? 'Selecionar todos' : 'Selecionar todos'}
              </span>
            </div>
          )}
          <div className="divide-y divide-gray-100">
            {items.map((item) => {
              const counts = item.task_counts || {};
              const checked = selectedIds.has(item.project_id);
              return (
                <div
                  key={item.project_id}
                  className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50/40"
                  data-testid={`diag-item-${item.project_id}`}
                >
                  {meta.canAutoFix ? (
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggle(item.project_id)}
                      data-testid={`diag-check-${item.project_id}`}
                    />
                  ) : (
                    <div className="w-4" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {item.project_number ? `${item.project_number} · ` : ''}{item.name}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{item.project_type || '—'}</span>
                      {item.client_name && <><span>·</span><span>{item.client_name}</span></>}
                      <span>·</span>
                      <span>{item.overall_percentage}% concluído</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 text-[11px]">
                    {Object.entries(counts).map(([s, n]) => (
                      <span key={s} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                        {s}: <strong>{n}</strong>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 shrink-0">
                    <span className="text-gray-400">{item.current_status || '—'}</span>
                    <span>→</span>
                    <strong style={{ color: GREEN }}>{item.suggested_status}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Diagnostics({ user }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [fixing, setFixing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/diagnostics/project-status-mismatch');
      setData(data);
      // Auto-expand the kind with most items
      const top = Object.entries(data.by_kind || {}).sort((a, b) => b[1] - a[1])[0];
      if (top) setExpanded(new Set([top[0]]));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar diagnóstico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/diagnostics/project-status-mismatch');
        if (cancelled) return;
        setData(data);
        const top = Object.entries(data.by_kind || {}).sort((a, b) => b[1] - a[1])[0];
        if (top) setExpanded(new Set([top[0]]));
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Erro ao carregar diagnóstico');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = data?.items || [];
  const byKind = items.reduce((acc, it) => {
    (acc[it.kind] = acc[it.kind] || []).push(it);
    return acc;
  }, {});

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllInKind = (kind, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      (byKind[kind] || []).forEach((it) => {
        if (select) next.add(it.project_id); else next.delete(it.project_id);
      });
      return next;
    });
  };

  const onExpand = (kind) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      return next;
    });
  };

  const autoFixableCount = items
    .filter((it) => KIND_META[it.kind]?.canAutoFix)
    .length;

  const applyFix = async (mode) => {
    let payload = {};
    if (mode === 'selected') {
      if (selectedIds.size === 0) {
        toast.error('Nenhum projeto selecionado');
        return;
      }
      payload = { project_ids: Array.from(selectedIds) };
    } else {
      if (!window.confirm(
        `Confirmar correção AUTOMÁTICA de ${autoFixableCount} projeto(s)? `
        + `Sugestões de "Concluído" NÃO são aplicadas automaticamente.`,
      )) return;
    }

    setFixing(true);
    try {
      const { data } = await api.post('/diagnostics/project-status-fix', payload);
      toast.success(`${data.applied} projeto(s) corrigido(s)`, {
        description: data.skipped_done_suggested?.length
          ? `${data.skipped_done_suggested.length} sugestão(ões) de "Concluído" foram preservadas (revise manualmente).`
          : undefined,
      });
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao corrigir');
    } finally {
      setFixing(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="diagnostics-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: '#E1F5EE', color: GREEN }}>
            <Activity className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">Coerência de Status</h1>
            <p className="text-sm text-gray-500">
              Identifica projetos cujo status diverge da atividade real das tarefas.
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="diagnostics-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {loading ? (
          <Card className="p-8 text-center text-sm text-gray-500">Analisando projetos...</Card>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center" data-testid="diagnostics-empty">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
            <div className="text-base font-medium text-gray-900">Tudo coerente</div>
            <div className="text-sm text-gray-500 mt-1">
              Nenhum projeto com status divergente da atividade das tarefas.
            </div>
          </Card>
        ) : (
          <>
            {/* Summary + actions */}
            <Card className="p-5 border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <div className="text-xs text-gray-500">Total de divergências</div>
                    <div className="text-2xl font-semibold text-gray-900">{data.total_mismatches}</div>
                  </div>
                  <div className="h-10 w-px bg-gray-200" />
                  <div className="flex flex-wrap gap-2 text-xs">
                    {Object.entries(data.by_kind || {}).map(([k, n]) => {
                      const meta = KIND_META[k];
                      return (
                        <Badge key={k} className={`${meta?.color || 'bg-gray-50 border-gray-200'} border`}>
                          {meta?.label || k}: <strong className="ml-1">{n}</strong>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => applyFix('selected')}
                    disabled={selectedIds.size === 0 || fixing}
                    variant="outline"
                    size="sm"
                    data-testid="diagnostics-fix-selected"
                  >
                    {fixing ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                    Corrigir selecionados ({selectedIds.size})
                  </Button>
                  <Button
                    onClick={() => applyFix('all')}
                    disabled={autoFixableCount === 0 || fixing}
                    size="sm"
                    style={{ backgroundColor: GREEN }}
                    className="text-white hover:opacity-90"
                    data-testid="diagnostics-fix-all"
                  >
                    {fixing ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                    Corrigir todos ({autoFixableCount})
                  </Button>
                </div>
              </div>
            </Card>

            {/* Groups */}
            <div className="space-y-3">
              {Object.entries(byKind).map(([kind, list]) => (
                <KindGroup
                  key={kind}
                  kind={kind}
                  items={list}
                  selectedIds={selectedIds}
                  onToggle={toggle}
                  onToggleAll={toggleAllInKind}
                  expanded={expanded.has(kind)}
                  onExpand={onExpand}
                />
              ))}
            </div>

            <Card className="p-4 border border-gray-200 bg-gray-50/50">
              <div className="text-xs text-gray-600 leading-relaxed">
                <strong className="text-gray-900">Como funciona:</strong> ao confirmar uma correção,
                o sistema atualiza o status do projeto e registra a alteração no log de auditoria
                (origem: <code>bulk_fix</code>). Sugestões do tipo <em>&quot;pronto para concluir&quot;</em> nunca
                são aplicadas automaticamente — você deve mudar o status manualmente, pois um
                projeto pode ter etapas fora do sistema (faturamento, protocolo final etc.).
                Daqui pra frente, quando uma tarefa virar &quot;Em andamento&quot; ou &quot;Concluída&quot;,
                o projeto pai será promovido automaticamente para <strong>Em Andamento</strong>.
              </div>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
